import { FoodsIcon, GenericAccordion, Typography } from "@foodpilot/foods";
import { Box, useTheme } from "@mui/material";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";
import { ModifyButton } from "./ModifyButton";
import { SupplierFormType } from "../../SupplierForm.types";
import type { SupplierFormAppearance } from "../../SupplierForm.types";

type InfoSectionProps = {
    appearance?: SupplierFormAppearance;
    supplier: SupplierFormType;
};
export const InfoSection = (props: InfoSectionProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { appearance = "legacy", supplier } = props;

    if (appearance === "legacy") {
        const border = `${theme.custom.strokes.containerDivider.width} ${theme.custom.strokes.containerDivider.style} ${theme.custom.strokes.containerDivider.color}`;

        return (
            <GenericAccordion
                headerIcon={<InfoOutlined />}
                headerText={t("general_information")}
                controls={<ModifyButton appearance="legacy" pageNumber={0} />}
            >
                <Box component="table" sx={{ borderCollapse: "collapse", width: "100%" }}>
                    <Box component="tbody">
                        {[
                            [t("nom_du_fournisseur"), supplier.name ?? "-"],
                            [t("code_fournisseur"), supplier.clientId ?? "-"],
                        ].map(([label, value], index) => (
                            <Box component="tr" key={label}>
                                <Box
                                    component="td"
                                    sx={{
                                        borderBottom: index === 1 ? 0 : border,
                                        padding: theme.spacing(2.5, 0),
                                        width: "50%",
                                    }}
                                >
                                    <Typography variant="big-bold">{label}</Typography>
                                </Box>
                                <Box
                                    component="td"
                                    sx={{ borderBottom: index === 1 ? 0 : border, padding: theme.spacing(2.5, 0) }}
                                >
                                    <Typography variant="body">{value}</Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </GenericAccordion>
        );
    }

    return (
        <GenericAccordion
            headerIcon={<FoodsIcon icon="information" size={3} />}
            headerText={t("general_information")}
            controls={<ModifyButton appearance="product-v2" pageNumber={0} />}
        >
            <Box sx={{ overflowX: "auto" }}>
                <Box
                    component="table"
                    aria-label={t("general_information")}
                    sx={{ borderCollapse: "collapse", tableLayout: "fixed", width: "100%" }}
                >
                    <Box component="tbody">
                        <SummaryRow label={t("nom_du_fournisseur")} value={supplier.name ?? "-"} />
                        <SummaryRow isLast label={t("code_fournisseur")} value={supplier.clientId ?? "-"} />
                    </Box>
                </Box>
            </Box>
        </GenericAccordion>
    );
};

type SummaryRowProps = {
    isLast?: boolean;
    label: string;
    value: string;
};

const SummaryRow = ({ isLast = false, label, value }: SummaryRowProps) => {
    const theme = useTheme();
    const border = `${theme.custom.strokes.containerDivider.width} ${theme.custom.strokes.containerDivider.style} ${theme.custom.strokes.containerDivider.color}`;

    return (
        <Box component="tr">
            <Box
                component="td"
                sx={{
                    borderBottom: isLast ? 0 : border,
                    padding: theme.spacing(2.5, 0),
                    width: "50%",
                }}
            >
                <Typography variant="body-semibold">{label}</Typography>
            </Box>
            <Box component="td" sx={{ borderBottom: isLast ? 0 : border, padding: theme.spacing(2.5, 0) }}>
                <Typography variant="body-regular">{value}</Typography>
            </Box>
        </Box>
    );
};
